import { Role } from '@/constants/roles';
import { AdvisorGroupSetting } from '@/store/settingsApi';

type UrlPathNames = 'Security';
type HelpTypeKey = 1 | 2 | 3 | 4;
type HelpTypeValue = 'wealthaccess' | 'customprefix' | 'customurl' | 'nohelp';

const helpFilesUtility = () => {
  const urls = {
    advisor:
      'https://wahelp.helpdocsonline.com/ejo2yu5vgt062pqu4ud9mk5h7v0wqi06',
    firm: 'https://wafirm.helpdocsonline.com/ejo2yu5vgt062pqu4ud9mk5h7v0wqi06',
    default: 'https://wa.helpdocsonline.com/ejo2yu5vgt062pqu4ud9mk5h7v0wqi06',
    custom: '/ejo2yu5vgt062pqu4ud9mk5h7v0wqi06',
  };

  const investor = {
    GettingStarted: '/#ID1697413',
    Start: '/#ID1695070',
    FAQ: '/#ID1697414',
    Security: '/#ID1763577',
    MintImport: '#ID1808369',
    '/': '/#ID1697443',
    '/BalanceSheet': '/#ID1697420',
    '/Portfolio/Accounts': '/#ID1697421',
    '/Portfolio/Holdings': '/#ID1697417',
    '/Portfolio/Diversification': '/#ID1697422',
    '/CashFlow': '/#ID1697424',
    '/Tools/Documents': '/#ID1697426',
    '/Tools/Team': '/#ID1697427',
    '/ManageAccounts': '/#ID1697428',
    '/Reports': '/#ID1763982',
  };

  const advisor = {
    GettingStarted: '/#ID1697442',
    Start: '/#ID1697436',
    FAQ: '/#ID1697441',
    '/': '/#ID1697444',
    //'/Clients': "/Clients",
    //'/Wealth/Accounts': "/wealth/accounts",
    //'/Wealth/Holdings': "/wealth/holdings",
    //'/Wealth/Diversification': "/wealth/diversification",
    //'/Wealth/BalanceSheet': "/wealth/balanceSheet",
    '/Setup/Overview': '/#ID1697442',
    '/Setup/Clients': '/#ID1697452',
    '/Setup/Clients/Groups': '/#ID1726692',
    '/Setup/Clients/Invitation': '/#ID1726704',
    '/Setup/Clients/HomeAway': '/#ID1726708',
    '/Setup/Accounts': '/#ID1697453',
    '/Setup/Accounts/AssetClassifications': '/#ID1907436',
    '/Setup/Accounts/AccountTags': '/#ID1698073',
    '/Setup/Configuration': '/#ID1697454',
    '/Setup/Configuration/Legal': '/#ID1697454',
    '/Setup/Configuration/Legal/Disclaimer': '/#ID1697454',
    '/Setup/Configuration/Diversifications': '/#ID1907435',
    '/Setup/Configuration/Dashboard': '/#ID1763481',
    '/Setup/Configuration/ContactInformation': '/#ID1727825',
    '/Setup/Configuration/QuickLinks': '/#ID1726717',
    '/Setup/Configuration/VaultCategories': '/#ID1726718',
    '/Setup/Integrations': '/#ID1727824',
    '/Documents': '/#ID1726665',
    '/Reports': '/#ID1763482',
    '/Reports/Business': '/#ID1763482',
  };

  const firm = {
    Start: '/#ID1855072',
    Terminology: '/#ID1859031',
    '/': '/#ID1859023',
    '/Advisors': '/#ID1859024',
    '/BI/Metrics': '/#ID1859025',
    '/BI/AUM': '/#ID1859026',
    '/ClientUsage/Activity': '/#ID1859027',
    '/ClientUsage/Content': '/#ID1859028',
    '/Setup/Firm': '/#ID1859032',
  };

  const helpfiles: Record<Role, any> = {
    investor: investor,
    firm: firm,
    advisor: advisor,
    // "business banker": advisor,
    // "commercial firm": firm,
    // estate: {
    //   GettingStarted: "/#ID1748187",
    //   Start: "/#ID1763964",
    //   FAQ: "/#ID1697414",
    //   "/Estate/Allocations": "/#ID1748193",
    //   "/Estate/Setup": "/#ID1748187",
    //   ...{ investor },
    // },
    owner: {},
    banker: {},
  };

  const enums = {
    helpType: {
      1: 'wealthaccess',
      2: 'customprefix',
      3: 'customurl',
      4: 'nohelp',
    } as Record<HelpTypeKey, HelpTypeValue>,
  };

  const isValidHelpTypeKey = (key: number): key is HelpTypeKey => {
    return key >= 1 && key <= 4 && Number.isInteger(key);
  };

  const investorHelpOptions = (currentRole: string) => {
    return (
      ['Investor', 'Estate', 'Business Owner']
        .map((x) => x.toLowerCase())
        .indexOf(currentRole) >= 0
    );
  };

  const getHelpType = (
    currentRole: string,
    advisorGroupSettings: AdvisorGroupSetting[],
  ) => {
    if (investorHelpOptions(currentRole)) {
      const setting = advisorGroupSettings.find(
        (x) => x.Key === 'InvestorHelpOption',
      )?.Value;

      const option: number = setting ? Number(setting) : 1;

      if (isValidHelpTypeKey(option)) {
        return enums.helpType[option];
      }

      return enums.helpType[1]; // fallback to default
    }

    return enums.helpType[1];
  };

  const getCustomHelpUrl = (
    currentRole: string,
    advisorGroupSettings: AdvisorGroupSetting[],
  ) => {
    if (investorHelpOptions(currentRole)) {
      const setting = advisorGroupSettings.find(
        (x) => x.Key === 'InvestorHelpUrl',
      )?.Value;
      return Boolean(setting);
    }

    return false;
  };

  const getUrlByRole = (currentRole: string) => {
    let base = '';

    switch (currentRole.toLowerCase()) {
      case 'advisor':
        base = urls.advisor;
        break;
      case 'firm':
        base = urls.firm;
        break;
      default:
        base = urls.default;
        break;
    }

    return base;
  };

  const buildUrl = ({
    pathName,
    currentRole,
    advisorGroupSettings,
  }: {
    pathName: UrlPathNames;
    currentRole: string;
    advisorGroupSettings: AdvisorGroupSetting[];
  }): string => {
    const helpType = getHelpType(currentRole, advisorGroupSettings);
    if (helpType === 'customurl' || helpType === 'nohelp') {
      return '';
    }

    let base = '';
    const index = currentRole.toLowerCase() as Role;
    let path = helpfiles[index][pathName];

    if (!path) {
      return '';
    }

    path = pathName;
    base = getUrlByRole(currentRole);

    if (getHelpType(currentRole, advisorGroupSettings) === 'customprefix') {
      base = getCustomHelpUrl(currentRole, advisorGroupSettings) + urls.custom;
    }

    return `${base}${path}`;
  };

  return {
    buildUrl,
  };
};

export default helpFilesUtility();
